import React, { useState, useEffect } from 'react';
import { 
  Box, 
  CircularProgress, 
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Typography 
} from '@mui/material';
import useExecuteScript from './hooks/useExecuteScript';
import ChatBox from "./ChatBox";

import './styles/analyze_report.css';
import './styles/initial_report.css';

const AssessmentViewer = ({ appId, assessmentId, threadId }) => {
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState('');
  const [thoughts, setThoughts] = useState('');
  const [error, setError] = useState(null);
  const [showReasoningDialog, setShowReasoningDialog] = useState(false);

  console.log('AssessmentViewer rendered with:', { appId, assessmentId, threadId }); // Debug log

  useExecuteScript(html);

  useEffect(() => {
    console.log('useEffect triggered with:', { appId, assessmentId, threadId }); // Debug log
    
    const fetchAssessment = async () => {
      if (!appId || !assessmentId) {
        console.log('Missing required props:', { appId, assessmentId }); // Debug log
        return;
      }

      try {
        setLoading(true);
        var url = '';
        if (assessmentId === 'initial') {
          console.log('Fetching initial assessment for appId:', appId); // Debug log
          url = `${process.env.REACT_APP_API_URL}/applications/${appId}/initial_assessments/`;
        } else {
          console.log('Fetching from:', `${process.env.REACT_APP_API_URL}/applications/${appId}/assessments/${assessmentId}`); // Debug log
          url = `${process.env.REACT_APP_API_URL}/applications/${appId}/assessments/${assessmentId}`;
        }

        const response = await fetch(
          url,
          { credentials: 'include' }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        // Store thoughts from the response
        setThoughts(data.thoughts || 'No reasoning information available for this assessment.');

        // Parse result_html.body if it's JSON
        try {
          if (data.result_html && data.result_html.body) {
            const parsedBody = JSON.parse(data.result_html.body);
            if (parsedBody.html) {
              setHtml(parsedBody.html);
            } else if (parsedBody.error) {
              setHtml(parsedBody.error);
            } else {
              throw new Error('No HTML content in parsed body');
            }
          } else if (typeof data.result_html === 'string') {
            setHtml(data.result_html);
          } else if (data.result_html && data.result_html.html) {
            setHtml(data.result_html.html);
          } else {
            throw new Error('Invalid response format');
          }
        } catch (parseError) {
          console.error('Error parsing result_html.body:', parseError);
          setError('Failed to parse assessment content');
        }
        
      } catch (err) {
        console.error('Error fetching assessment:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (appId && assessmentId) {
      fetchAssessment();
    }
  }, [appId, assessmentId]);

  // Function to handle showing reasoning dialog
  const handleShowReasoning = () => {
    setShowReasoningDialog(true);
  };

  // Function to handle closing reasoning dialog
  const handleCloseReasoning = () => {
    setShowReasoningDialog(false);
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Box>
      <Paper sx={{ mt: 3, p: 2, background: "#f9f9f9" }}>
        <div dangerouslySetInnerHTML={{ __html: html }} />
        
        {/* Add the Show Reasoning button directly at the bottom */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={handleShowReasoning}
            sx={{ 
              backgroundColor: '#f0f0f0',
              '&:hover': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            Show Reasoning
          </Button>
        </Box>
      </Paper>
      
      {threadId && (
        <Paper sx={{ p: 3, mb: 3, boxShadow: 3 }}>
          <ChatBox 
            threadId={threadId} 
            sx={{ minHeight: '300px' }}  // Add minimum height
          />
        </Paper>
      )}

      {/* Reasoning Dialog */}
      <Dialog 
        open={showReasoningDialog} 
        onClose={handleCloseReasoning}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assessment Reasoning
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
            {thoughts}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReasoning} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessmentViewer;